import type { ParentType } from 'src/rbac/Permission';

/**
 * Which register a finding / rating form is writing results into.
 *
 * The three finding forms (assessment, oversight, internal audit) share the
 * same selectors, but each writes a DIFFERENT set of result tables and is
 * therefore governed by a different set of perm_v2 resource types.
 */
export type ResultFamily = 'assessment' | 'internal-audit' | 'oversight';

interface ResultPermissionTypes {
  document: ParentType;
  obligation: ParentType;
  riskControlled: ParentType;
  riskUncontrolled: ParentType;
  controlTest: ParentType;
  impact: ParentType;
}

/**
 * perm_v2 resource type per register per result kind.
 *
 * The selectors used to check the first-line (`assessment`) names for all
 * three registers, so an oversight or internal-audit form asked "may I insert
 * a `test_result`?" — a type held by ControlManager, not by the oversight /
 * internal-audit roles. That both hid the option from the roles that own the
 * register and showed it to roles that do not (COI-639). The data-layer
 * processors check the same per-register types.
 *
 * Only the PERMISSION types are per-register. The option values the selectors
 * emit stay on the first-line `Parent_Type_Enum` members: those are the
 * `ResultType` discriminator the forms and `oversight_result_parent` rows use
 * as one shared keyspace (COI-631).
 */
export const resultPermissionTypes: Record<
  ResultFamily,
  ResultPermissionTypes
> = {
  assessment: {
    document: 'document_assessment_result',
    obligation: 'obligation_assessment_result',
    riskControlled: 'risk_assessment_result',
    riskUncontrolled: 'risk_assessment_result',
    controlTest: 'test_result',
    impact: 'impact_rating',
  },
  oversight: {
    document: 'document_oversight_result',
    obligation: 'obligation_oversight_result',
    riskControlled: 'risk_controlled_oversight_result',
    riskUncontrolled: 'risk_uncontrolled_oversight_result',
    controlTest: 'control_test_oversight_result',
    impact: 'impact_oversight_rating',
  },
  'internal-audit': {
    document: 'document_internal_audit_result',
    obligation: 'obligation_internal_audit_result',
    riskControlled: 'risk_controlled_internal_audit_result',
    riskUncontrolled: 'risk_uncontrolled_internal_audit_result',
    controlTest: 'control_test_internal_audit_result',
    impact: 'impact_internal_audit_rating',
  },
};
